import { Head } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import OnboardingShell, {
  type OnboardingShellBrand,
} from "@/components/onboarding/onboarding-shell"
import { Spinner } from "@/components/ui/spinner"

export interface OauthCallbackProps {
  /** Brand chrome — `null` only when BE couldn't resolve the invite
   *  token at all (very edge case; the page degrades gracefully without
   *  the chrome). */
  brand: OnboardingShellBrand | null
  /** Test-only override for the 8s "taking longer" hint. Production
   *  callers omit it. */
  taking_longer_delay_ms?: number
}

/**
 * OauthCallback — server-rendered interstitial mounted at
 * `GET /oauth/social/callback` (OKS-379 — provider-neutral callback path).
 *
 * UX Spec §2:
 *   - Centered card matching OKS-22 chrome, brand logo retained.
 *   - Spinner + "Verifying your TikTok account…" heading.
 *   - `<div role="status" aria-live="polite" aria-busy="true">` so SR
 *     users get the audible announcement on mount.
 *   - **No CTAs** — autoroutes within ≤3s on success (BE handles).
 *   - **Slow-path UX:** at 8s elapsed (client-side), reveal a "Taking
 *     longer than usual…" hint to reduce perceived stall.
 *   - **Hard timeout:** 10s server-side. If BE hasn't redirected by
 *     then, BE serves the network_error or state_expired page directly
 *     — FE doesn't need to do client-side timeout work.
 *
 * The component renders only on the slow-path: BE's controller does
 * the OAuth code exchange in the request lifecycle and 302s direct on
 * the fast path (per BE TRD §"OAuth callback flow"). This page only
 * appears when the exchange takes long enough that BE chose to render
 * an interstitial frame instead of holding the response open.
 *
 * a11y:
 *   - `role="status" aria-live="polite"` on the announcement region.
 *   - `aria-busy="true"` while the spinner is up.
 *   - Heading focused on mount (mirrors OKS-21/22 pattern).
 *   - The "Taking longer…" hint is also `aria-live="polite"` so SR
 *     users hear the update without a route change.
 */
export default function OauthCallback({
  brand,
  taking_longer_delay_ms = 8000,
}: OauthCallbackProps) {
  const { t } = useTranslation()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [showTakingLonger, setShowTakingLonger] = useState(false)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowTakingLonger(true)
    }, taking_longer_delay_ms)
    return () => clearTimeout(timeout)
  }, [taking_longer_delay_ms])

  // Defensive: if BE can't resolve the invite, render without the shell
  // (still a usable interstitial — spinner + status copy).
  const content = (
    <div
      data-testid="oauth-callback-interstitial"
      className="flex flex-1 flex-col items-center justify-center gap-4 py-12 text-center"
    >
      <Spinner className="size-8" />
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-testid="oauth-callback-status"
        className="flex flex-col gap-2"
      >
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-xl font-semibold tracking-tight outline-none"
        >
          {t("tiktok.oauth_callback.heading")}
        </h1>
        {showTakingLonger && (
          <p
            className="text-muted-foreground text-sm"
            aria-live="polite"
            data-testid="oauth-callback-taking-longer"
          >
            {t("tiktok.oauth_callback.taking_longer")}
          </p>
        )}
      </div>
    </div>
  )

  return (
    <>
      <Head title={t("tiktok.oauth_callback.head_title")} />
      {brand ? (
        <OnboardingShell brand={brand} step={null} total={4}>
          {content}
        </OnboardingShell>
      ) : (
        <main
          tabIndex={-1}
          className="bg-background flex min-h-svh flex-col items-center justify-center px-4 py-10"
        >
          {content}
        </main>
      )}
    </>
  )
}
